import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiResponse } from "@/types";
import type {
  ICreateOfferPayload,
  IHandoffSummary,
  IOffer,
  IOnboardHireResult,
  IUpdateOfferPayload,
  IListOffersParams,
  IOfferCounts,
  IOfferEarningComponent,
  IOfferLetterSettings,
  IUpdateOfferLetterSettingsPayload,
} from "@/types/(company)/recruitment.interface";
import { companyRecruitmentApi } from "./api.recruitment";

/**
 * Tell the pipeline an offer changed.
 *
 * Offers live in their own `createApi`, and RTK Query tags do not cross that
 * boundary — an `invalidatesTags` here can never reach the candidate detail or
 * the board, however it is written. So the offer endpoints looked correctly
 * invalidated while the rest of recruitment quietly went stale: an accepted
 * offer left the candidate header reading "Interview" with a "Move to Offer"
 * button, over an application the server had already closed as hired.
 *
 * Dispatching the other API's own invalidation is the supported way across
 * slices. Kept in one helper because every offer write means the same thing to
 * the pipeline: this candidate moved.
 */
const invalidatePipeline = (dispatch: (action: unknown) => unknown) => {
  dispatch(
    companyRecruitmentApi.util.invalidateTags([
      "RecruitmentApplication",
      { type: "RecruitmentCandidate", id: "LIST" },
      { type: "RecruitmentJob", id: "LIST" },
    ]),
  );
};

export const recruitmentOffersApi = createApi({
  reducerPath: "recruitmentOffersApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["Offer", "Handoff", "OfferLetterSettings"],
  endpoints: (builder) => ({
    getOffers: builder.query<IApiResponse<IOffer[]>, IListOffersParams | void>({
      query: (params) => ({ url: APIEndpoints.Offers.Base, params: params ?? {} }),
      providesTags: (result) =>
        result
          ? [...result.data.map((o) => ({ type: "Offer" as const, id: o.id })), { type: "Offer" as const, id: "LIST" }]
          : [{ type: "Offer" as const, id: "LIST" }],
    }),

    // Tagged with the list, so every create, send or response refreshes the tiles.
    getOfferCounts: builder.query<IApiResponse<IOfferCounts>, void>({
      query: () => ({ url: APIEndpoints.Offers.Counts }),
      providesTags: [{ type: "Offer", id: "LIST" }],
    }),

    getOffer: builder.query<IApiResponse<IOffer>, number>({
      query: (id) => ({ url: APIEndpoints.Offers.ById(id) }),
      providesTags: (_r, _e, id) => [{ type: "Offer", id }],
    }),

    createOffer: builder.mutation<IApiResponse<IOffer>, ICreateOfferPayload>({
      query: (body) => ({ url: APIEndpoints.Offers.Base, method: "POST", body }),
      invalidatesTags: [{ type: "Offer", id: "LIST" }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    updateOfferLetter: builder.mutation<IApiResponse<IOffer>, { id: number; letterHtml: string }>({
      query: ({ id, letterHtml }) => ({ url: APIEndpoints.Offers.Letter(id), method: "PATCH", body: { letterHtml } }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Offer", id }],
    }),

    /**
     * The only edit path — the offer-form page's fields, re-saved. The letter
     * is rebuilt server-side from these, same as on create; there is no way to
     * send raw HTML here.
     */
    updateOffer: builder.mutation<
      IApiResponse<IOffer>,
      { id: number } & IUpdateOfferPayload
    >({
      query: ({ id, ...body }) => ({ url: APIEndpoints.Offers.ById(id), method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Offer", id }, { type: "Offer", id: "LIST" }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    sendOffer: builder.mutation<IApiResponse<IOffer>, number>({
      query: (id) => ({ url: APIEndpoints.Offers.Send(id), method: "POST" }),
      invalidatesTags: (_r, _e, id) => [{ type: "Offer", id }, { type: "Offer", id: "LIST" }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    recordOfferResponse: builder.mutation<IApiResponse<IOffer>, { id: number; accepted: boolean; signed?: boolean }>({
      query: ({ id, ...body }) => ({ url: APIEndpoints.Offers.Response(id), method: "POST", body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Offer", id }, { type: "Offer", id: "LIST" }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        invalidatePipeline(dispatch);
      },
    }),

    getHandoff: builder.query<IApiResponse<IHandoffSummary>, number>({
      query: (applicationId) => ({ url: APIEndpoints.Offers.Handoff(applicationId) }),
      providesTags: (_r, _e, applicationId) => [{ type: "Handoff", id: applicationId }],
    }),

    /**
     * Onboard the hire into the HRMS — the hand-off panel's one click.
     *
     * Invalidates its own hand-off so the panel re-reads and switches from
     * "pending" to the created employee. It does not invalidate the pipeline:
     * the candidate was already hired when the offer was accepted, and this
     * only creates the employee record on this side.
     */
    onboardHire: builder.mutation<IApiResponse<IOnboardHireResult>, number>({
      query: (applicationId) => ({
        url: APIEndpoints.Offers.Onboard(applicationId),
        method: "POST",
      }),
      invalidatesTags: (_r, _e, applicationId) => [{ type: "Handoff", id: applicationId }],
    }),

    /** The payroll components an allowance line can become on hire. */
    getOfferEarningComponents: builder.query<
      IApiResponse<IOfferEarningComponent[]>,
      void
    >({
      query: () => ({ url: APIEndpoints.Offers.EarningComponents }),
    }),

    /*
      Settings → Offer letters. Every write answers with the whole settings
      shape, so each one replaces the cache rather than refetching it.
    */
    getOfferLetterSettings: builder.query<IApiResponse<IOfferLetterSettings>, void>({
      query: () => ({ url: APIEndpoints.Offers.LetterSettings }),
      providesTags: ["OfferLetterSettings"],
    }),

    updateOfferLetterSettings: builder.mutation<
      IApiResponse<IOfferLetterSettings>,
      IUpdateOfferLetterSettingsPayload
    >({
      query: (body) => ({ url: APIEndpoints.Offers.LetterSettings, method: "PUT", body }),
      invalidatesTags: ["OfferLetterSettings"],
    }),

    setOfferStamp: builder.mutation<IApiResponse<IOfferLetterSettings>, Blob>({
      query: (file) => {
        const body = new FormData();
        body.append("stamp", file, file instanceof File ? file.name : "stamp.png");
        return { url: APIEndpoints.Offers.LetterStamp, method: "PUT", body };
      },
      invalidatesTags: ["OfferLetterSettings"],
    }),

    removeOfferStamp: builder.mutation<IApiResponse<IOfferLetterSettings>, void>({
      query: () => ({ url: APIEndpoints.Offers.LetterStamp, method: "DELETE" }),
      invalidatesTags: ["OfferLetterSettings"],
    }),

    saveOfferSignatory: builder.mutation<
      IApiResponse<IOfferLetterSettings>,
      { id?: number; name: string; title?: string; signature?: Blob }
    >({
      query: ({ id, name, title, signature }) => {
        const body = new FormData();
        body.append("name", name);
        body.append("title", title ?? "");
        if (signature) body.append("signature", signature, "signature.png");
        return id
          ? { url: APIEndpoints.Offers.SignatoryById(id), method: "PATCH", body }
          : { url: APIEndpoints.Offers.Signatories, method: "POST", body };
      },
      invalidatesTags: ["OfferLetterSettings"],
    }),

    removeOfferSignatory: builder.mutation<IApiResponse<IOfferLetterSettings>, number>({
      query: (id) => ({ url: APIEndpoints.Offers.SignatoryById(id), method: "DELETE" }),
      invalidatesTags: ["OfferLetterSettings"],
    }),
  }),
});

export const {
  useGetOffersQuery,
  useGetOfferCountsQuery,
  useGetOfferQuery,
  useCreateOfferMutation,
  useUpdateOfferLetterMutation,
  useUpdateOfferMutation,
  useSendOfferMutation,
  useRecordOfferResponseMutation,
  useGetHandoffQuery,
  useOnboardHireMutation,
  useGetOfferEarningComponentsQuery,
  useGetOfferLetterSettingsQuery,
  useUpdateOfferLetterSettingsMutation,
  useSetOfferStampMutation,
  useRemoveOfferStampMutation,
  useSaveOfferSignatoryMutation,
  useRemoveOfferSignatoryMutation,
} = recruitmentOffersApi;
